/**
 * Web Speech narration provider (docs/BUILD-OVERRIDES.md, §8.4).
 *
 * Two jobs, one implementation:
 *
 *  1. **Editor preview** — the wizard speaks a line so the author hears the
 *     pacing before Kokoro has baked anything.
 *  2. **Runtime fallback** — `BUILD-OVERRIDES.md` overrides §8.4: a
 *     `say`/`narrate`/`question` cue whose `audio` is `null` (TTS container
 *     down, model still downloading, bundle baked without audio — which is
 *     exactly what the live bundle of story `01M34BJ3DRY5YGKVK5TF210F52`
 *     looks like today: every cue carries `"audio": null`) still plays.
 *     Word highlighting then uses proportional timing computed here, and the
 *     line is marked `timing: 'estimated'` so the puppet renderer knows not
 *     to gate the mouth on guessed word boundaries (ADDENDUM §5.1).
 *
 * Nothing in this file blocks: when `speechSynthesis` is missing the caller
 * gets an immediately-resolvable handle and the story runs silently on the
 * estimated timing. A child must never be stuck in front of a frozen fox
 * because a browser has no voices installed.
 *
 * Pure helpers (`chunkText`, `splitWords`, `estimateWords`,
 * `estimateLineDuration`) are exported and unit-tested; the class around
 * them only talks to the platform.
 */

/* ------------------------------------------------------------- word model */

/**
 * One word of a line, seconds, with its slice of the source text.
 *
 * Field-for-field `Word` of ARCHITECTURE.md §4.6.3 and
 * `frontend/src/schema/story.ts#Word` — `s`/`e`, not `t0`/`t1`. See
 * `normalizeWords()` in `audio.ts` for the tolerated alias.
 */
export interface LineWord {
  w: string
  /** Start, seconds from the line's own start. */
  s: number
  /** End, seconds. */
  e: number
  /** Char index into the line text. */
  ci: number
  /** Char length. */
  cl: number
}

/** Baseline narration rate: the `line_est = words / 2.1 + 0.4` of §4.6.4 / ADDENDUM §2.7.4. */
export const BASE_WORDS_PER_SECOND = 2.1
/** The speed those 2.1 words/s were measured at (band 3-4 TTS speed, §4.6.1). */
export const BASE_SPEED = 0.85
/** Constant tail of the estimate: the breath at the end of a line. */
export const LINE_TAIL_S = 0.4

/** `true` when this browser can speak at all. Never throws on a server render. */
export function isSpeechAvailable(): boolean {
  try {
    return typeof globalThis !== 'undefined' && 'speechSynthesis' in globalThis && typeof (globalThis as { SpeechSynthesisUtterance?: unknown }).SpeechSynthesisUtterance === 'function'
  } catch {
    return false
  }
}

const WORD_RE = /\S+/g

/** Split a line into words, keeping each word's `ci`/`cl` slice of the source text. */
export function splitWords(text: string): { w: string; ci: number; cl: number }[] {
  const out: { w: string; ci: number; cl: number }[] = []
  if (!text) return out
  WORD_RE.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = WORD_RE.exec(text)) !== null) {
    out.push({ w: m[0], ci: m.index, cl: m[0].length })
  }
  return out
}

/** Word count used by every duration estimate in the stack. */
export function countWords(text: string): number {
  return splitWords(text).length
}

/**
 * Seconds a line takes when nothing has been baked: `words / rate + 0.4`
 * with `rate = 2.1 × (speed / 0.85)`, so the band's TTS speed still shortens
 * the line exactly as `compile.py` assumed when it wrote `dur`.
 */
export function estimateLineDuration(text: string, speed: number = BASE_SPEED, wordsPerSecond: number = BASE_WORDS_PER_SECOND): number {
  const words = countWords(text)
  if (words === 0) return 0
  const safeSpeed = Number.isFinite(speed) && speed > 0 ? speed : BASE_SPEED
  const rate = wordsPerSecond * (safeSpeed / BASE_SPEED)
  return Math.round((words / Math.max(0.2, rate) + LINE_TAIL_S) * 1000) / 1000
}

/** Relative time weight of a word: longer words and words that end a clause take longer. */
function wordWeight(w: string): number {
  let weight = w.length + 1
  if (/[,;:]$/.test(w)) weight += 2
  if (/[.!?…]$/.test(w)) weight += 3
  return weight
}

/**
 * Proportional word timings across `dur` seconds — the karaoke track for a
 * line with no baked `words[]`. Deterministic: the same text and duration
 * always produce the same spans, so a seek lands on the same word as
 * playing through (§8.1).
 */
export function estimateWords(text: string, dur: number): LineWord[] {
  const tokens = splitWords(text)
  if (tokens.length === 0) return []
  const total = Number.isFinite(dur) && dur > 0 ? dur : estimateLineDuration(text)
  let sum = 0
  const weights = tokens.map((t) => {
    const weight = wordWeight(t.w)
    sum += weight
    return weight
  })
  if (sum <= 0) sum = tokens.length
  const out: LineWord[] = []
  let acc = 0
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]!
    const s = (acc / sum) * total
    acc += weights[i] ?? 1
    const e = (acc / sum) * total
    out.push({ w: t.w, ci: t.ci, cl: t.cl, s: Math.round(s * 1000) / 1000, e: Math.round(e * 1000) / 1000 })
  }
  return out
}

/* ---------------------------------------------------------------- chunking */

/** A speakable slice of a line plus its char offset in the source text. */
export interface SpeechChunk {
  text: string
  /** Char offset of `text` inside the original line — `onboundary.charIndex` is relative to the chunk. */
  start: number
}

/**
 * Chrome (and every Chromium browser since ~33) stops speaking after roughly
 * 15 seconds of a single utterance, and Android WebView truncates long ones
 * outright. So a line is spoken as a sequence of short utterances split at
 * sentence ends, then at clause commas, then — only if one clause is still
 * too long — at word boundaries. Each chunk keeps its offset so an
 * `onboundary` event can be mapped back to a word index of the whole line.
 */
export function chunkText(text: string, maxChars = 180): SpeechChunk[] {
  const trimmedStart = text.search(/\S/)
  if (trimmedStart < 0) return []
  const limit = Math.max(16, maxChars)
  const sentences = splitKeepingOffsets(text, /[^.!?…]+[.!?…]*\s*/g)
  const out: SpeechChunk[] = []

  const pushPiece = (piece: SpeechChunk): void => {
    if (piece.text.trim().length === 0) return
    if (piece.text.length <= limit) {
      out.push(piece)
      return
    }
    // Too long even as one sentence: split on words.
    const words = splitWords(piece.text)
    let cur = ''
    let curStart = piece.start + (words[0]?.ci ?? 0)
    for (const word of words) {
      const candidate = cur.length === 0 ? word.w : `${cur} ${word.w}`
      if (candidate.length > limit && cur.length > 0) {
        out.push({ text: cur, start: curStart })
        cur = word.w
        curStart = piece.start + word.ci
      } else {
        if (cur.length === 0) curStart = piece.start + word.ci
        cur = candidate
      }
    }
    if (cur.length > 0) out.push({ text: cur, start: curStart })
  }

  let pending: SpeechChunk | null = null
  for (const sentence of sentences) {
    const piece = { text: sentence.text.trimEnd(), start: sentence.start }
    if (piece.text.length === 0) continue
    if (pending && pending.text.length + 1 + piece.text.length <= limit) {
      pending = { text: `${pending.text} ${piece.text}`, start: pending.start }
      continue
    }
    if (pending) pushPiece(pending)
    pending = piece
  }
  if (pending) pushPiece(pending)
  return out
}

function splitKeepingOffsets(text: string, re: RegExp): SpeechChunk[] {
  const out: SpeechChunk[] = []
  const rx = new RegExp(re.source, re.flags.includes('g') ? re.flags : `${re.flags}g`)
  let m: RegExpExecArray | null
  while ((m = rx.exec(text)) !== null) {
    if (m[0].length === 0) {
      rx.lastIndex += 1
      continue
    }
    out.push({ text: m[0], start: m.index })
  }
  if (out.length === 0 && text.length > 0) out.push({ text, start: 0 })
  return out
}

/* ---------------------------------------------------------------- provider */

export interface SpeakOptions {
  /** Narration speed — the script's `audio.speed` / `CastEntry.speed` (0.85–0.95). */
  speed?: number
  lang?: string
  /** `SpeechSynthesisVoice.voiceURI` to prefer, when the parent picked one. */
  voiceURI?: string
  pitch?: number
  volume?: number
  /** Fired with the index into `splitWords(text)` whenever the engine reports a boundary. */
  onWord?: (index: number, charIndex: number) => void
  onStart?: () => void
  /** Fired once, whether the line finished, errored or was cancelled. */
  onEnd?: (reason: 'end' | 'error' | 'cancel') => void
}

export interface SpeakHandle {
  /** Stop speaking now; `onEnd('cancel')` still fires exactly once. */
  cancel(): void
  /** Resolves when the line is done, for whatever reason. Never rejects. */
  readonly done: Promise<'end' | 'error' | 'cancel'>
  /** `false` when this browser cannot speak — the caller then runs on estimated timing alone. */
  readonly speaking: boolean
}

const CHROME_KEEPALIVE_MS = 9000

function isChromium(): boolean {
  try {
    const ua = (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent ?? ''
    return /Chrome|Chromium|CriOS|Edg/.test(ua) && !/Firefox/.test(ua)
  } catch {
    return false
  }
}

/**
 * Speaks lines through `window.speechSynthesis`, chunked and gesture-safe.
 *
 * The provider owns no story state: it reports word boundaries and the end
 * of a line, and everything else (which word to highlight, when the cue is
 * over) stays with `runner.ts` / `subtitles.ts`, whose timing is pure.
 */
export class WebSpeechProvider {
  private readonly synth: SpeechSynthesis | null
  private readonly makeUtterance: (text: string) => SpeechSynthesisUtterance
  private current: { cancel: () => void } | null = null
  private keepAlive: ReturnType<typeof setInterval> | null = null
  private warmed = false
  /** Playback speed (0.5-2); multiplies the line's own narration speed. */
  private voiceSpeed = 1

  /**
   * `synth` and `makeUtterance` are injectable so the provider can be driven
   * by a fake engine in vitest — jsdom implements neither.
   */
  constructor(synth?: SpeechSynthesis | null, makeUtterance?: (text: string) => SpeechSynthesisUtterance) {
    if (synth !== undefined) {
      this.synth = synth
    } else {
      this.synth = isSpeechAvailable() ? (globalThis as unknown as { speechSynthesis: SpeechSynthesis }).speechSynthesis : null
    }
    this.makeUtterance = makeUtterance ?? ((text: string) => new SpeechSynthesisUtterance(text))
  }

  get available(): boolean {
    return this.synth !== null
  }

  /**
   * The playback speed (0.5×-2×, the control bar's menu), as a multiplier on
   * the speed a line asks for. It reaches the engine as `utterance.rate` on
   * the **next** chunk: the Web Speech API cannot re-rate an utterance that is
   * already speaking, and cancelling mid-sentence to apply it would be worse
   * for a child than one slow sentence. `AudioBus.setRate` forwards here.
   */
  setVoiceSpeed(rate: number): void {
    this.voiceSpeed = Number.isFinite(rate) && rate > 0 ? Math.min(2, Math.max(0.5, rate)) : 1
  }

  get voiceRate(): number {
    return this.voiceSpeed
  }

  /** Voices, which several engines only populate after `voiceschanged`. */
  voices(): SpeechSynthesisVoice[] {
    try {
      return this.synth?.getVoices() ?? []
    } catch {
      return []
    }
  }

  /**
   * Call inside the first user gesture (the same tap that unlocks Web Audio,
   * §8.4). Safari and Chrome refuse the first `speak()` otherwise; speaking
   * one empty utterance there makes every later line work.
   */
  warmup(): void {
    if (this.warmed || !this.synth) return
    this.warmed = true
    try {
      const u = this.makeUtterance(' ')
      u.volume = 0
      this.synth.speak(u)
      this.synth.cancel()
    } catch {
      /* a browser that refuses the warm-up still speaks later, or does not speak at all */
    }
  }

  speak(text: string, opts: SpeakOptions = {}): SpeakHandle {
    this.cancel()
    const chunks = chunkText(text)
    const tokens = splitWords(text)
    let settle: (r: 'end' | 'error' | 'cancel') => void = () => {}
    const done = new Promise<'end' | 'error' | 'cancel'>((resolve) => {
      settle = resolve
    })

    if (!this.synth || chunks.length === 0) {
      // No engine (or nothing to say): finish immediately so the runner keeps
      // its own clock and the story plays silently. Never block on audio.
      queueMicrotask(() => {
        opts.onEnd?.('end')
        settle('end')
      })
      return { cancel: () => {}, done, speaking: false }
    }

    const synth = this.synth
    let index = 0
    let finished = false
    let started = false
    let lastWord = -1

    const finish = (reason: 'end' | 'error' | 'cancel'): void => {
      if (finished) return
      finished = true
      this.stopKeepAlive()
      if (this.current === handle) this.current = null
      opts.onEnd?.(reason)
      settle(reason)
    }

    const speakChunk = (): void => {
      if (finished) return
      const chunk = chunks[index]
      if (!chunk) {
        finish('end')
        return
      }
      index += 1
      let utterance: SpeechSynthesisUtterance
      try {
        utterance = this.makeUtterance(chunk.text)
      } catch {
        finish('error')
        return
      }
      if (opts.lang) utterance.lang = opts.lang
      utterance.rate = clampRate((opts.speed ?? 1) * this.voiceSpeed)
      utterance.pitch = opts.pitch ?? 1
      utterance.volume = opts.volume ?? 1
      const voice = opts.voiceURI ? this.voices().find((v) => v.voiceURI === opts.voiceURI) : undefined
      if (voice) utterance.voice = voice
      utterance.onstart = (): void => {
        if (started) return
        started = true
        opts.onStart?.()
      }
      utterance.onboundary = (event: SpeechSynthesisEvent): void => {
        if (event.name && event.name !== 'word') return
        const global = chunk.start + (event.charIndex ?? 0)
        const wordIndex = wordIndexForChar(tokens, global)
        if (wordIndex >= 0 && wordIndex !== lastWord) {
          lastWord = wordIndex
          opts.onWord?.(wordIndex, global)
        }
      }
      utterance.onend = (): void => {
        if (finished) return
        if (index >= chunks.length) finish('end')
        else speakChunk()
      }
      utterance.onerror = (): void => {
        // A refused or interrupted utterance is not a story failure: drop the
        // rest of the line and let the runner's own clock carry the cue.
        if (!finished) finish('error')
      }
      try {
        synth.speak(utterance)
      } catch {
        finish('error')
      }
    }

    const handle: SpeakHandle & { cancel: () => void } = {
      cancel: () => {
        if (finished) return
        try {
          synth.cancel()
        } catch {
          /* ignore */
        }
        finish('cancel')
      },
      done,
      speaking: true,
    }
    this.current = handle
    this.startKeepAlive()
    speakChunk()
    return handle
  }

  cancel(): void {
    const cur = this.current
    this.current = null
    cur?.cancel()
    this.stopKeepAlive()
  }

  pause(): void {
    try {
      this.synth?.pause()
    } catch {
      /* ignore */
    }
  }

  resume(): void {
    try {
      this.synth?.resume()
    } catch {
      /* ignore */
    }
  }

  /**
   * Chromium silently stops a long queue after ~15 s. A `pause()/resume()`
   * pair every 9 s keeps it alive. This is a wall-clock timer on the speech
   * engine only — it never touches the story clock or the scene graph, which
   * stay pure per §8.1.
   */
  private startKeepAlive(): void {
    if (this.keepAlive !== null || !this.synth || !isChromium()) return
    this.keepAlive = setInterval(() => {
      try {
        if (this.synth?.speaking) {
          this.synth.pause()
          this.synth.resume()
        }
      } catch {
        /* ignore */
      }
    }, CHROME_KEEPALIVE_MS)
  }

  private stopKeepAlive(): void {
    if (this.keepAlive === null) return
    clearInterval(this.keepAlive)
    this.keepAlive = null
  }
}

function clampRate(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return 1
  return Math.min(2, Math.max(0.5, speed))
}

/** Index into `splitWords(text)` for a char offset; `-1` before the first word. */
export function wordIndexForChar(tokens: readonly { ci: number; cl: number }[], charIndex: number): number {
  let found = -1
  for (let i = 0; i < tokens.length; i += 1) {
    const t = tokens[i]!
    if (charIndex >= t.ci) found = i
    else break
  }
  return found
}
